const TIME_FORMAT_KEY = "timeFormatPreference";
const DEFAULT_DRIVER_KEY = "defaultDriverPreference";
const RIGHT_GRAPH_COUNT_KEY = "raceGraphCount";
const GRAPH_SELECTIONS_KEY = "raceGraphSelections";
const LEFT_GRAPH_COUNT_KEY = "raceLeftGraphCount";

export function getTimeFormatPreference(): "seconds" | "minutes" {
  const stored = localStorage.getItem(TIME_FORMAT_KEY);
  return stored === "minutes" ? "minutes" : "seconds";
}

export function setTimeFormatPreference(format: "seconds" | "minutes"): void {
  localStorage.setItem(TIME_FORMAT_KEY, format);
}

export function getDefaultDriverPreference(): string | null {
  return localStorage.getItem(DEFAULT_DRIVER_KEY);
}

export function setDefaultDriverPreference(carColour: string | null): void {
  if (carColour === null) {
    localStorage.removeItem(DEFAULT_DRIVER_KEY);
  } else {
    localStorage.setItem(DEFAULT_DRIVER_KEY, carColour);
  }
}

export function getRightGraphCountPreference(): number {
  const stored = localStorage.getItem(RIGHT_GRAPH_COUNT_KEY);
  const parsed = stored ? Number(stored) : NaN;
  return [1, 2, 3].includes(parsed) ? parsed : 3;
}

export function setRightGraphCountPreference(count: number): void {
  localStorage.setItem(RIGHT_GRAPH_COUNT_KEY, String(count));
}

export function getLeftGraphCountPreference(): number {
  const stored = localStorage.getItem(LEFT_GRAPH_COUNT_KEY);
  const parsed = stored ? Number(stored) : NaN;
  return [1, 2, 3].includes(parsed) ? parsed : 1;
}

export function setLeftGraphCountPreference(count: number): void {
  localStorage.setItem(LEFT_GRAPH_COUNT_KEY, String(count));
}

export function getGraphSelectionPreference(id: number): string | null {
  const stored = localStorage.getItem(GRAPH_SELECTIONS_KEY);
  const selections = stored ? (JSON.parse(stored) as string[]) : [];
  return selections[id - 1] ?? null;
}

export function setGraphSelectionPreference(id: number, label: string): void {
  const stored = localStorage.getItem(GRAPH_SELECTIONS_KEY);
  const selections = stored ? (JSON.parse(stored) as string[]) : [];
  selections[id - 1] = label;
  localStorage.setItem(GRAPH_SELECTIONS_KEY, JSON.stringify(selections));
}

export function getPreferredFormattedTimeMs(ms: number): string {
  const format = getTimeFormatPreference();
  const totalSeconds = ms / 1000;

  if (format === "minutes") {
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, "0")}:${secs
        .toFixed(3)
        .padStart(6, "0")}`;
    }
    if (mins > 0) {
      return `${mins}:${secs.toFixed(3).padStart(6, "0")}`;
    }
    return secs.toFixed(3);
  }

  return totalSeconds.toFixed(3);
}

export function getPreferredFormattedTimeSeconds(
  seconds: number,
  padDecimals = true,
  dp = 3,
) {
  const format = getTimeFormatPreference();
  const padLength = dp > 0 ? 3 + dp : 2;

  const trimDecimals = (str: string) => {
    if (padDecimals) return str;
    // Remove trailing zeros after decimal, but keep at least one decimal if present
    return str.replace(/(\.[0-9]*?)0+$/, "$1").replace(/\.$/, "");
  };

  if (format === "minutes") {
    const isNegative = seconds < 0;
    const absSeconds = Number(Math.abs(seconds).toFixed(dp));
    const sign = isNegative ? "-" : "";
    const hours = Math.floor(absSeconds / 3600);
    const mins = Math.floor((absSeconds % 3600) / 60);
    const secs = absSeconds % 60;

    if (hours > 0) {
      return `${sign}${hours}:${mins
        .toString()
        .padStart(2, "0")}:${trimDecimals(
        secs.toFixed(dp).padStart(padLength, "0"),
      )}`;
    }
    if (mins > 0) {
      return `${sign}${mins}:${trimDecimals(
        secs.toFixed(dp).padStart(padLength, "0"),
      )}`;
    }
    return `${sign}${trimDecimals(secs.toFixed(dp))}`;
  }

  return trimDecimals(seconds.toFixed(dp));
}

export function formatSessionTime(ms: number): string {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function formatTimestampWithMs(epochMs: number | null): string {
  if (epochMs === null) return "-";
  const d = new Date(epochMs);
  const time = d.toLocaleTimeString("en-GB");
  const ms = d.getMilliseconds().toString().padStart(3, "0");
  return `${time}.${ms}`;
}

// Parse lap time from either format: "92.500" or "1:32.500" to seconds
export function parseLapTimeToSeconds(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Check for M:SS.mmm format
  if (trimmed.includes(":")) {
    const parts = trimmed.split(":");
    if (parts.length === 2) {
      // M:SS.mmm
      const mins = parseInt(parts[0], 10);
      const secs = parseFloat(parts[1]);
      if (!isNaN(mins) && !isNaN(secs)) {
        return mins * 60 + secs;
      }
    }
    return null;
  }

  // Plain seconds
  const seconds = parseFloat(trimmed);
  return isNaN(seconds) ? null : seconds;
}
